import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  FilterSchema,
  ParagraafSchemaObjectStatus,
  SubparagraafSchemaObjectStatus,
  VoorschriftSchemaObjectStatus,
} from '@/api/generated/digiVAPI.schemas';
import { useLinksInHoofdstuk } from '@/api/queries/objects';

import { EditorView } from '../-store/useEditorStore';

type UseEditorLinksProps = {
  hoofdstukId: string;
  besluitId: string;
  view: EditorView;
};

export const getEditorLinksQueryOptions = ({
  hoofdstukId,
  besluitId,
  view,
}: UseEditorLinksProps) => {
  const VSPBFilter: FilterSchema[] = [
    { model: 'Voorschrift', field: 'Volgend', op: 'is_null', value: '' },
  ];

  const SPPBFilter: FilterSchema[] = [
    { model: 'Subparagraaf', field: 'Volgend', op: 'is_null', value: '' },
  ];

  const PHBFilter: FilterSchema[] = [
    { model: 'Paragraaf', field: 'Volgend', op: 'is_null', value: '' },
  ];

  if (view !== EditorView.RECOVER) {
    VSPBFilter.push({
      model: 'Voorschrift',
      field: 'ObjectStatus',
      op: 'ne',
      value: VoorschriftSchemaObjectStatus.IngetrokkenGepubliceerd,
    });

    SPPBFilter.push({
      model: 'Subparagraaf',
      field: 'ObjectStatus',
      op: 'ne',
      value: SubparagraafSchemaObjectStatus.IngetrokkenGepubliceerd,
    });

    PHBFilter.push({
      model: 'Paragraaf',
      field: 'ObjectStatus',
      op: 'ne',
      value: ParagraafSchemaObjectStatus.IngetrokkenGepubliceerd,
    });
  }

  return useLinksInHoofdstuk.getOptions(
    {
      hoofdstukId,
      besluitId,
      VSPBFilter,
      SPPBFilter,
      PHBFilter,
    },
    {
      enabled: !!hoofdstukId && !!besluitId,
    }
  );
};

export const useEditorLinks = ({
  hoofdstukId,
  besluitId,
  view,
}: UseEditorLinksProps) => {
  const options = useMemo(() => {
    return getEditorLinksQueryOptions({
      hoofdstukId,
      besluitId,
      view,
    });
  }, [hoofdstukId, besluitId, view]);

  return useQuery(options);
};

useEditorLinks.getOptions = getEditorLinksQueryOptions;

export const useEditorLinksSuspense = ({
  hoofdstukId,
  besluitId,
  view,
}: UseEditorLinksProps) => {
  const options = useMemo(() => {
    return getEditorLinksQueryOptions({
      hoofdstukId,
      besluitId,
      view,
    });
  }, [hoofdstukId, besluitId, view]);

  return useSuspenseQuery(options);
};
